'use client';

/**
 * CopyButton
 *
 * A shared, self-contained "copy to clipboard" button that handles:
 *  - The actual clipboard write (with a safe fallback for non-secure contexts)
 *  - Success feedback (swaps to a Check icon / "Copied!" label for a short window)
 *  - Error handling (swaps to an alert icon briefly via an optional callback)
 *
 * Three visual variants cover every surface in the app:
 *  - `icon`         — square icon-only button with a light border (e.g. contact
 *                     rows in the booking details sidebar).
 *  - `inline`       — flat icon (no button chrome) for contexts embedded in a
 *                     sentence (e.g. the Stripe test-mode notice).
 *  - `outline-pill` — labelled pill ("Copy URL" ↔ "Copied!") used in the share
 *                     profile and calendar sync dialogs.
 *
 * The component intentionally centralises this UX so every copy interaction
 * across the product feels identical.
 */
import { Check, Copy } from 'lucide-react';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@ui/lib/utils';
import { copyToClipboard } from '@ui/utils/shareUtils';

import { Button } from '../../ui/button';

export type CopyButtonVariant = 'icon' | 'inline' | 'outline-pill';
export type CopyButtonSize = 'sm' | 'md';

export interface CopyButtonProps {
  /** The text that should be written to the clipboard. */
  value: string;
  /** Label shown in the `outline-pill` variant (ignored for the icon/inline variants). */
  label?: string;
  /** Label shown while the success feedback is active. Defaults to "Copied!". */
  copiedLabel?: string;
  /** Visual style. Defaults to `icon`. */
  variant?: CopyButtonVariant;
  /** Size. Only applies to the `icon` and `outline-pill` variants. */
  size?: CopyButtonSize;
  /** Disable the button (skips the copy). */
  disabled?: boolean;
  /** How long the "Copied!" state lingers before reverting. Defaults to 2000 ms. */
  feedbackDurationMs?: number;
  /** Called after the clipboard write succeeds. */
  onCopy?: (value: string) => void;
  /** Called when the clipboard write throws. */
  onError?: (error: unknown) => void;
  /** Extra classes merged into the root button. */
  className?: string;
  /** Native tooltip used when idle. Defaults to the label or a generic string. */
  title?: string;
  /** Native tooltip used while success feedback is active. Defaults to `copiedLabel`. */
  copiedTitle?: string;
  /** aria-label override (required when the button has no visible label, i.e. icon-only). */
  'aria-label'?: string;
  /** Button type attribute. Defaults to `button` to avoid accidental form submits. */
  type?: 'button' | 'submit' | 'reset';
  /**
   * Stop click/pointer propagation so enclosing rows (like a booking card) do
   * not trigger their own click handler when the button is used. Defaults to
   * `true`.
   */
  stopPropagation?: boolean;
  /**
   * Optional ref to the underlying `<button>` element. Useful when an ancestor
   * (e.g. a clickable URL pill) wants to forward its click to the button.
   */
  buttonRef?: React.Ref<HTMLButtonElement>;
}

const DEFAULT_FEEDBACK_DURATION_MS = 2000;
const DEFAULT_COPIED_LABEL = 'Copied!';
const DEFAULT_TITLE = 'Copy to clipboard';

export function CopyButton({
  value,
  label,
  copiedLabel = DEFAULT_COPIED_LABEL,
  variant = 'icon',
  size = 'sm',
  disabled = false,
  feedbackDurationMs = DEFAULT_FEEDBACK_DURATION_MS,
  onCopy,
  onError,
  className,
  title,
  copiedTitle,
  type = 'button',
  stopPropagation = true,
  buttonRef,
  ...rest
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up any pending timer if the component unmounts mid-feedback so we
  // don't call setState on an unmounted component.
  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const handleCopy = useCallback(
    async (e?: React.MouseEvent) => {
      if (stopPropagation && e) e.stopPropagation();
      if (disabled) return;
      if (!value) return;
      try {
        await copyToClipboard(value);
        setCopied(true);
        onCopy?.(value);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setCopied(false), feedbackDurationMs);
      } catch (err) {
        onError?.(err);
      }
    },
    [disabled, feedbackDurationMs, onCopy, onError, stopPropagation, value]
  );

  const handlePointerDown = (e: React.PointerEvent) => {
    if (stopPropagation) e.stopPropagation();
  };

  const resolvedTitle = copied ? copiedTitle || copiedLabel : title || label || DEFAULT_TITLE;
  const ariaLabel = rest['aria-label'] || resolvedTitle;

  // ---- inline variant: flat icon, no button chrome ----------------------
  if (variant === 'inline') {
    return (
      <button
        ref={buttonRef}
        type={type}
        onPointerDown={handlePointerDown}
        onClick={handleCopy}
        disabled={disabled}
        aria-label={ariaLabel}
        title={resolvedTitle}
        className={cn(
          'inline-flex items-center justify-center p-1 rounded transition-colors',
          'hover:bg-neutral-100 disabled:opacity-60 disabled:cursor-not-allowed',
          className
        )}
      >
        {copied ? (
          <Check className="w-3.5 h-3.5 text-success-600" strokeWidth={3} />
        ) : (
          <Copy className="w-3.5 h-3.5 text-neutral-600" />
        )}
      </button>
    );
  }

  // ---- outline-pill variant: labelled button with icon + text -----------
  if (variant === 'outline-pill') {
    const sizeClasses = size === 'md' ? 'h-10 px-4 text-sm' : 'h-9 px-3 text-sm';
    return (
      <Button
        ref={buttonRef}
        type={type}
        variant={copied ? 'default' : 'outline'}
        onPointerDown={handlePointerDown}
        onClick={handleCopy}
        disabled={disabled}
        aria-label={ariaLabel}
        title={resolvedTitle}
        className={cn(
          'gap-2 font-medium transition-colors',
          sizeClasses,
          copied
            ? 'bg-success-500 hover:bg-success-600 border-success-500 text-white'
            : 'text-neutral-900 hover:bg-neutral-50',
          className
        )}
      >
        {copied ? <Check className="w-4 h-4" strokeWidth={3} /> : <Copy className="w-4 h-4" />}
        <span>{copied ? copiedLabel : label || 'Copy'}</span>
      </Button>
    );
  }

  // ---- icon variant (default): square button with border ----------------
  const iconSizeClasses = size === 'md' ? 'w-10 h-10' : 'w-8 h-8';
  const iconGlyphSize = size === 'md' ? 'w-4 h-4' : 'w-3 h-3';
  return (
    <Button
      ref={buttonRef}
      type={type}
      variant="ghost"
      size="sm"
      onPointerDown={handlePointerDown}
      onClick={handleCopy}
      disabled={disabled}
      aria-label={ariaLabel}
      title={resolvedTitle}
      className={cn('p-0 border border-neutral-200 hover:bg-neutral-100 shrink-0', iconSizeClasses, className)}
    >
      {copied ? (
        <Check className={cn(iconGlyphSize, 'text-success-600')} strokeWidth={3} />
      ) : (
        <Copy className={cn(iconGlyphSize, 'text-neutral-900')} />
      )}
    </Button>
  );
}
